import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useFeatures } from './featureContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart3, FileDown, Star, Printer, Search } from 'lucide-react';
import { fmtMoney } from './money';
import ReportDocument from './ui/ReportRenderer';
import { PacketPanel } from './ui/packets';
import { PRESETS, PRESET_LABEL, isNonprofit, catalog, GROUP_ORDER, COLUMN_OPTIONS, COMPARE_OPTIONS, sectionMap, netLabels, MANAGEMENT_REPORTS, ESSENTIALS } from './reportCatalog';


export default function Reports({ base, isDark = true, company, onOpenTxn, go, initialReport }) {
  const np = isNonprofit(company);
  const feat = useFeatures();
  const CAT = useMemo(() => catalog(np).filter((d) => (d.feature ? feat[d.feature] : (feat.all_reports || ESSENTIALS.has(d.id || d.k)))), [np, feat]);
  const SECTIONS = useMemo(() => sectionMap(np), [np]);
  const NETLBLS = useMemo(() => netLabels(np), [np]);
  const favKey = `olfin_fav_${company?.company_id || 'x'}`;
  const [mode, setMode] = useState('standard'); // standard | custom | management | budgets
  const [favs, setFavs] = useState([]);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(null); // catalog def
  const [preset, setPreset] = useState('all');
  const AS_OF_REPORTS = useMemo(() => new Set(['payroll_liability_balances', 'inventory_valuation_summary', 'inventory_stock_status', 'ar_aging_summary', 'ar_aging_detail', 'ap_aging_summary', 'ap_aging_detail', 'customer_balance_summary', 'customer_balance_detail', 'vendor_balance_summary', 'vendor_balance_detail', 'collections_report', 'open_invoices']), []);
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [start, setStart] = useState(''); const [end, setEnd] = useState('');
  const [basis, setBasis] = useState(company?.accounting_method === 'cash' ? 'cash' : 'accrual');
  const [columns, setColumns] = useState('total'); const [compare, setCompare] = useState('none');
  const [compact, setCompact] = useState(false); const [note, setNote] = useState('');
  const [showAllRows, setShowAllRows] = useState(false);
  const [data, setData] = useState(null); const [busy, setBusy] = useState(false);
  const [memName, setMemName] = useState('');
  const [mem, setMem] = useState([]); const [budgets, setBudgets] = useState([]);
  const [names, setNames] = useState({}); const [drill, setDrill] = useState(null);
  const [reportStyle, setReportStyle] = useState(null);
  useEffect(() => { axios.get(`${base}/settings`).then((r) => setReportStyle(r.data?.reports || null)).catch(() => {}); }, [base]);
  const genAt = useMemo(() => new Date().toLocaleString(), [data]);
  useEffect(() => { setBasis(company?.accounting_method === 'cash' ? 'cash' : 'accrual'); }, [company?.accounting_method]);

  useEffect(() => {
    axios.get(`${base}/accounts`).then((r) => {
      const m = {}; (r.data?.accounts || []).forEach((a) => { m[a.account_id] = a.full_name; }); setNames(m);
    }).catch(() => {});
    axios.get(`${base}/reports/memorized`).then((r) => setMem(r.data || [])).catch(() => {});
    axios.get(`${base}/budgets`).then((r) => setBudgets(r.data || [])).catch(() => {});
    // server-backed favorites (per user); migrate legacy localStorage once
    axios.get(`${base}/reports/favorites`).then((r) => {
      const ids = r.data?.ids || [];
      if (ids.length === 0) {
        let local = []; try { local = JSON.parse(localStorage.getItem(favKey) || '[]'); } catch { local = []; }
        if (local.length) {
          axios.put(`${base}/reports/favorites`, { ids: local })
            .then(() => { setFavs(local); localStorage.removeItem(favKey); }).catch(() => {});
          return;
        }
      }
      setFavs(ids);
    }).catch(() => {});
  }, [base]);

  const toggleFav = (id) => {
    const n = favs.includes(id) ? favs.filter((x) => x !== id) : [...favs, id];
    setFavs(n);
    axios.put(`${base}/reports/favorites`, { ids: n }).catch(() => {});
  };
  const paramsFor = (def) => {
    const p = { preset, basis, columns: def.params?.columns || columns, ...(def.params || {}) };
    if (compare !== 'none' && !def.params?.columns) p.compare = compare;
    if (preset === 'custom') { if (start) p.start = start; if (end) p.end = end; }
    if (AS_OF_REPORTS.has(def.k)) p.as_of = asOf;
    return p;
  };
  // fill From/To boxes from the backend's fiscal-year-aware resolved range (set in run())
  const run = useCallback(async (def) => {
    if (def.k === 'business_snapshot' && go) { go('business_snapshot'); return; }
    setActive(def); setBusy(true); setDrill(null); setData(null);
    try {
      const res = (await axios.post(`${base}/reports/run`, { report: def.k, params: paramsFor(def) })).data;
      setData(res);
      if (res && res.resolved_start != null && res.resolved_end != null) {
        if (res.resolved_start !== start) setStart(res.resolved_start);
        if (res.resolved_end !== end) setEnd(res.resolved_end);
      }
    } catch (e) { setData({ error: e?.response?.data?.detail || 'Failed to run report' }); }
    setBusy(false);
  }, [base, preset, basis, columns, compare, start, end, asOf, go]);
  useEffect(() => { if (active) run(active); /* eslint-disable-next-line */ }, [preset, basis, columns, compare, start, end, asOf]);
  // deep-link: auto-open a specific report when asked (e.g. from Business Snapshot)
  useEffect(() => {
    if (!initialReport) return;
    const def = CAT.find((d) => d.k === initialReport) || { k: initialReport, label: initialReport };
    run(def);
    // eslint-disable-next-line
  }, [initialReport]);

  const runMem = async (m) => {
    setActive({ k: m.report, id: m.id, label: m.name }); setBusy(true); setData(null); setDrill(null);
    try { setData((await axios.post(`${base}/reports/run`, { report: m.report, params: m.params })).data); }
    catch (e) { setData({ error: e?.response?.data?.detail || 'Failed' }); } setBusy(false);
  };
  // Budgets live in AD Finance ▸ Budget (one source); these reports read them with actuals from the books.
  const BUDGET_LABEL = { budget_vs_actual: 'Budget vs. Actuals', budget_overview: 'Budget Overview' };
  const runBudget = (b, k = 'budget_vs_actual', view) => run({ k, id: b?.id || k, label: BUDGET_LABEL[k],
    params: { budget_id: b?.id || '', view: view || active?.params?.view || 'total' } });
  const isBudget = !!(active && BUDGET_LABEL[active.k]);
  const drillLine = async (ids) => {
    const parts = await Promise.all((ids || []).map((a) => axios.get(`${base}/reports/drilldown`, { params: { account_id: a, start: data?.start || '', end: data?.end || '' } }).then((r) => r.data).catch(() => null)));
    const rows = parts.filter(Boolean).flatMap((p) => p.rows || []).sort((x, y) => String(x.date || '').localeCompare(String(y.date || '')));
    setDrill({ rows });
  };
  const exportIt = async (fmt) => {
    const r = await axios.post(`${base}/reports/export`, { report: active.k, params: paramsFor(active), fmt }, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a');
    a.href = url; a.download = `${active.id}.${fmt === 'xlsx' ? 'xlsx' : fmt}`; a.click(); URL.revokeObjectURL(url);
  };
  const memorize = async () => { await axios.post(`${base}/reports/memorized`, { name: memName || active.label, report: active.k, params: paramsFor(active) }); setMemName(''); axios.get(`${base}/reports/memorized`).then((r) => setMem(r.data || [])); };
  const doDrill = async (accountId) => {
    const r = await axios.get(`${base}/reports/drilldown`, { params: { account_id: accountId, start: preset === 'custom' ? start : '', end: preset === 'custom' ? end : end } });
    setDrill(r.data);
  };

  const grouped = useMemo(() => {
    const ql = q.toLowerCase();
    const match = (d) => !ql || d.label.toLowerCase().includes(ql);
    const g = {}; GROUP_ORDER.forEach((x) => { g[x] = []; });
    CAT.forEach((d) => { if (match(d)) g[d.g].push(d); });
    g.Favorites = CAT.filter((d) => favs.includes(d.id) && match(d));
    return g;
  }, [CAT, q, favs]);

  const longDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '');
  const periodLabel = (isBudget && data?.start)
    ? `Fiscal year ${longDate(data.start)} – ${longDate(data.end)}`
    : (preset === 'custom' ? `${start} – ${end}` : (PRESET_LABEL[preset] || ''));
  const card = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';

  return (
    <div className="flex gap-4" data-testid="olfin-reports-center">
      <aside className={`w-48 shrink-0 rounded-lg border ${card} p-3 space-y-1 h-fit`}>
        <div className="flex items-center gap-2 mb-2"><BarChart3 className="h-5 w-5 text-emerald-500" /><span className="font-semibold">Reports</span></div>
        {[['standard', 'Standard reports'], ['custom', 'Custom reports'], ['management', 'Management reports'], ['budgets', 'Budgets']].map(([k, l]) => (
          <button key={k} onClick={() => setMode(k)} data-testid={`reports-mode-${k}`}
            className={`block w-full text-left px-2 py-1.5 rounded text-sm ${mode === k ? 'bg-emerald-600 text-white' : 'hover:bg-slate-500/10'}`}>{l}</button>
        ))}
      </aside>

      <section className="flex-1 min-w-0 space-y-3">
        <h2 className="text-xl font-semibold">Reports &amp; Analytics</h2>
        {!active && mode === 'standard' && (
          <>
            <div className="relative max-w-md"><Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
              <Input className="pl-8" placeholder="Type report name here" value={q} onChange={(e) => setQ(e.target.value)} data-testid="reports-search" /></div>
            {GROUP_ORDER.map((g) => grouped[g].length > 0 && (
              <div key={g} className={`rounded-lg border ${card} p-3`} data-testid={`reports-group-${g.replace(/\s/g, '-')}`}>
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">{g}{g === 'Nonprofit & 1099' && go && <button className="ml-3 normal-case tracking-normal text-emerald-600 hover:underline" onClick={() => go('np_setup')} data-testid="reports-np-setup">Nonprofit setup</button>}</p>
                <div className="grid sm:grid-cols-2 gap-x-6">
                  {grouped[g].map((d) => (
                    <div key={`${g}-${d.id}`} className="flex items-center gap-2 py-1">
                      <button onClick={() => toggleFav(d.id)} data-testid={`report-fav-${d.id}`} title="Favorite">
                        <Star className={`h-4 w-4 ${favs.includes(d.id) ? 'fill-amber-400 text-amber-400' : 'text-slate-400'}`} />
                      </button>
                      <button onClick={() => run(d)} data-testid={`report-pick-${d.id}`} className="text-sm text-emerald-600 hover:underline text-left">{d.label}</button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
        {!active && mode === 'custom' && (
          <div className={`rounded-lg border ${card} p-3`} data-testid="reports-custom">
            {mem.length === 0 ? <p className="text-sm text-slate-500">No memorized reports yet. Run a report, then “Save customization”.</p>
              : mem.map((m) => (
                <div key={m.id} className="flex items-center gap-2 py-1">
                  <button className="text-sm text-emerald-600 hover:underline flex-1 text-left" onClick={() => runMem(m)} data-testid={`report-mem-${m.id}`}>{m.name}</button>
                  <button className="text-xs text-red-400" onClick={async () => { await axios.delete(`${base}/reports/memorized/${m.id}`); axios.get(`${base}/reports/memorized`).then((r) => setMem(r.data || [])); }} data-testid={`report-mem-del-${m.id}`}>Remove</button>
                </div>
              ))}
          </div>
        )}
        {!active && mode === 'management' && (
          <div className={`rounded-lg border ${card} p-3 space-y-2`} data-testid="reports-management">
            {feat.report_packets && <PacketPanel base={base} CAT={CAT} defaults={MANAGEMENT_REPORTS} card="border-transparent p-0" />}
            <p className="text-xs text-slate-500">Or open one of them:</p>
            <div className="flex flex-wrap gap-2">
              {MANAGEMENT_REPORTS.map((k) => {
                const d = CAT.find((x) => x.k === k) || { label: k };
                return <Button key={k} size="sm" variant="outline" onClick={() => run(d)} data-testid={`report-mgmt-${k}`}>{d.label}</Button>;
              })}
            </div>
          </div>
        )}
        {!active && mode === 'budgets' && (
          <div className={`rounded-lg border ${card} p-3 space-y-1`} data-testid="reports-budgets">
            <p className="text-xs text-slate-500">Budgets are planned in AD Finance ▸ Budget; actuals come from these books.</p>
            {budgets.length === 0 ? <p className="text-sm text-slate-500">No budgets yet.</p>
              : budgets.map((b) => (
                <div key={b.id} className="flex items-center gap-3 text-sm py-1" data-testid={`report-budget-${b.id}`}>
                  <span className="font-medium">{b.name}</span>
                  <span className="text-xs text-slate-500">{b.season || `FY ${b.fiscal_year}`} · v{b.version} · {b.status}{b.sport ? ` · ${b.sport}` : ''}</span>
                  <button className="text-emerald-600 hover:underline" onClick={() => runBudget(b, 'budget_vs_actual')} data-testid={`report-budget-bva-${b.id}`}>Budget vs. Actuals</button>
                  <button className="text-emerald-600 hover:underline" onClick={() => runBudget(b, 'budget_overview')} data-testid={`report-budget-ov-${b.id}`}>Budget Overview</button>
                </div>
              ))}
          </div>
        )}

        {active && (
          <div className={`rounded-lg border ${card} p-3 space-y-3`} data-testid="report-viewer">
            <button onClick={() => { setActive(null); setData(null); setDrill(null); }} className="text-sm text-emerald-600 hover:underline" data-testid="report-back">&larr; Back to standard reports</button>
            <h3 className="text-lg font-semibold">{active.label}</h3>
            <div className="flex items-end gap-2 flex-wrap">
              {isBudget ? (
                <>
                  <label className="space-y-1"><span className="text-xs text-slate-500">Budget</span>
                    <select className="block h-9 rounded-md border border-slate-300 dark:border-slate-600 bg-transparent px-2 text-sm" value={data?.budget?.id || ''}
                      onChange={(e) => runBudget({ id: e.target.value }, active.k)} data-testid="report-budget-pick">
                      {(data?.budgets || []).map((b) => <option key={b.id} value={b.id}>{b.name} · v{b.version} · {b.status}</option>)}
                    </select></label>
                  <label className="space-y-1"><span className="text-xs text-slate-500">Columns</span>
                    <select className="block h-9 rounded-md border border-slate-300 dark:border-slate-600 bg-transparent px-2 text-sm" value={active.params?.view || 'total'}
                      onChange={(e) => runBudget({ id: data?.budget?.id }, active.k, e.target.value)} data-testid="report-budget-view">
                      <option value="total">Total</option><option value="quarter">Quarters</option><option value="budget">Budgets (by sport) + total</option>
                    </select></label>
                  {data?.fiscal_year != null && <span className="text-xs text-slate-500 pb-2">Fiscal year {data.start} → {data.end} · {data.basis === 'cash' ? 'Cash' : 'Accrual'} basis (company setting)</span>}
                </>
              ) : AS_OF_REPORTS.has(active.k) ? (
                <label className="space-y-1"><span className="text-xs text-slate-500">As of</span>
                  <Input type="date" className="w-40" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="report-as-of" /></label>
              ) : (
                <>
                  <label className="space-y-1"><span className="text-xs text-slate-500">Report period</span>
                    <Select value={preset} onValueChange={setPreset}><SelectTrigger className="w-44" data-testid="report-preset"><SelectValue /></SelectTrigger><SelectContent>{PRESETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></label>
                  <label className="space-y-1"><span className="text-xs text-slate-500">From</span>
                    <Input type="date" className="w-36" value={start} onChange={(e) => { setStart(e.target.value); setPreset('custom'); }} data-testid="report-start" /></label>
                  <label className="space-y-1"><span className="text-xs text-slate-500">To</span>
                    <Input type="date" className="w-36" value={end} onChange={(e) => { setEnd(e.target.value); setPreset('custom'); }} data-testid="report-end" /></label>
                </>
              )}
              {!isBudget && <>
              <label className="space-y-1"><span className="text-xs text-slate-500">Accounting method</span>
                <Select value={basis} onValueChange={setBasis}><SelectTrigger className="w-28" data-testid="report-basis"><SelectValue /></SelectTrigger><SelectContent>{[['cash', 'Cash'], ['accrual', 'Accrual']].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></label>
              <label className="space-y-1"><span className="text-xs text-slate-500">Columns</span>
                <Select value={columns} onValueChange={setColumns}><SelectTrigger className="w-32" data-testid="report-columns"><SelectValue /></SelectTrigger><SelectContent>{COLUMN_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></label>
              <label className="space-y-1"><span className="text-xs text-slate-500">Compare to</span>
                <Select value={compare} onValueChange={setCompare}><SelectTrigger className="w-36" data-testid="report-compare"><SelectValue /></SelectTrigger><SelectContent>{COMPARE_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></label>
              </>}
              <Button variant="outline" size="sm" onClick={() => setCompact((v) => !v)} data-testid="report-compact">{compact ? 'Normal' : 'Compact'}</Button>
              <Button variant="outline" size="sm" onClick={() => run(active)} data-testid="report-refresh">Refresh</Button>
              <Button variant="outline" size="sm" onClick={() => exportIt('xlsx')} data-testid="report-xlsx"><FileDown className="h-4 w-4 mr-1" />Excel</Button>
              <Button variant="outline" size="sm" onClick={() => exportIt('pdf')} data-testid="report-pdf">PDF</Button>
              <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="report-print"><Printer className="h-4 w-4 mr-1" />Print</Button>
            </div>
            <div className="flex items-end gap-2 flex-wrap rounded bg-slate-500/5 p-2" data-testid="report-customize-drawer">
              <Button size="sm" variant={showAllRows ? 'default' : 'outline'} onClick={() => setShowAllRows((v) => !v)} data-testid="report-show-all-rows">{showAllRows ? 'Showing all rows' : 'Show all rows'}</Button>
              <Input className="w-48" placeholder="Save customization as…" value={memName} onChange={(e) => setMemName(e.target.value)} data-testid="report-mem-name" />
              <Button size="sm" variant="outline" onClick={memorize} data-testid="report-memorize"><Star className="h-4 w-4 mr-1" />Save As</Button>
            </div>
            {busy && <p className="text-sm text-slate-500">Running…</p>}
            {data?.error && <p className="text-sm text-red-400" data-testid="report-error">{data.error}</p>}
            {data && !data.error && (
              <>
                <ReportDocument rep={data} title={active.label} company={company?.name} periodLabel={periodLabel}
                  basis={['profit_loss', 'profit_loss_detail', 'profit_loss_by_customer', 'profit_loss_pct', 'balance_sheet', 'balance_sheet_detail'].includes(active.k) ? basis : null}
                  compact={compact} names={names} onDrill={doDrill} onDrillLine={drillLine} onOpenTxn={onOpenTxn} sections={SECTIONS} netLbls={NETLBLS} showAllRows={showAllRows} style={reportStyle} />
                <div className="text-xs text-slate-500 border-t border-slate-300 dark:border-slate-700 mt-2 pt-1" data-testid="report-footer">
                  {basis === 'cash' ? 'Cash' : 'Accrual'} basis | {genAt}
                </div>
                <Input className="w-full mt-1" placeholder="Add note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="report-note" />
              </>
            )}
            {drill && (
              <div className={`rounded border ${card} p-2`} data-testid="report-drill">
                <div className="flex justify-between items-center mb-1"><span className="text-sm font-semibold">Transaction Detail by Account — {fmtMoney(drill.total)}</span>
                  <button className="text-xs text-slate-400" onClick={() => setDrill(null)} data-testid="report-drill-back">Back</button></div>
                <ReportDocument rep={{ rows: drill.rows }} title="Transaction Detail by Account" periodLabel={periodLabel} onOpenTxn={onOpenTxn} compact />
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
